import React, { useState, useEffect, useMemo } from 'react';
import { Modal, List, Button, Popconfirm, Typography, Slider, App, Input } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSoundscapesManager } from '../../hooks/useSoundscapesManager';
import { useEditModal } from '../../hooks/useEditModal';
import type { Soundscape } from '../../types/types';

interface SoundscapeModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const SoundscapeModal: React.FC<SoundscapeModalProps> = ({ isOpen, onClose }) => {
  const { soundscapes, deleteSoundscape } = useSoundscapesManager();
  const { showEditModal } = useEditModal();
  const { message } = App.useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSound, setActiveSound] = useState<{ id: number; audio: HTMLAudioElement } | null>(null);

  const handleDeleteSoundscape = async (soundId: number) => {
    const soundName = (soundscapes ?? []).find((s: Soundscape) => s.id === soundId)?.name ?? 'Soundscape';
    await deleteSoundscape(soundId);
    message.info(`"${soundName}" has been deleted.`);
  };

  const filteredSoundscapes = useMemo(() => {
    if (!searchTerm) {
      return soundscapes;
    }
    const lowercasedFilter = searchTerm.toLowerCase();
    return (soundscapes ?? []).filter((sound: Soundscape) =>
      sound.name.toLowerCase().includes(lowercasedFilter)
    );
  }, [soundscapes, searchTerm]);

  useEffect(() => {
    // This effect manages the lifecycle of the audio element.
    // It returns a cleanup function that will be called when the component
    // unmounts or when the activeSound dependency changes.
    return () => {
      activeSound?.audio.pause();
    };
  }, [activeSound]);

  const handlePlay = (sound: Soundscape) => {
    if (activeSound?.id === sound.id) {
      activeSound?.audio.pause();
      setActiveSound(null);
    } else {
      if (activeSound) {
        activeSound.audio.pause();
      }
      const newAudio = new Audio(sound.url);
      newAudio.loop = true;
      newAudio.play();
      setActiveSound({ id: sound.id, audio: newAudio });
    }
  };

  const handleVolumeChange = (value: number) => {
    if (activeSound) {
      activeSound.audio.volume = value / 100;
    }
  };

  return (
    <>
      <Modal
        title="Soundscapes"
        open={isOpen}
        onCancel={onClose}
        footer={[
          <Button key="add" icon={<PlusOutlined />} onClick={() => showEditModal({ type: 'soundscape', item: null })}>
            Add Soundscape
          </Button>,
          <Button key="close" type="primary" onClick={onClose}>
            Close
          </Button>,
        ]}
        // Reset search when modal is closed
        afterClose={() => setSearchTerm('')}
      >
        <Input.Search
          placeholder="Search soundscapes..."
          onChange={e => setSearchTerm(e.target.value)}
          style={{ marginBottom: '1rem' }}
          allowClear
        />
        <div style={{ marginBottom: '1rem' }}>
          <Typography.Text>
            Now Playing: {activeSound ? (soundscapes ?? []).find((s: Soundscape) => s.id === activeSound.id)?.name : 'None'}
          </Typography.Text>
          <Slider
            defaultValue={100}
            onChange={handleVolumeChange}
            disabled={!activeSound}
            tooltip={{ open: false }}
          />
        </div>
        <List
          itemLayout="horizontal"
          dataSource={filteredSoundscapes}
          renderItem={(sound: Soundscape) => (
            <List.Item
              actions={[
                <Button
                  shape="circle"
                  icon={activeSound?.id === sound.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  onClick={() => handlePlay(sound)}
                />,
                <Popconfirm
                  title="Delete this soundscape?"
                  onConfirm={() => handleDeleteSoundscape(sound.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button shape="circle" icon={<DeleteOutlined />} danger />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta title={sound.name} />
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};

export default SoundscapeModal;